import React, { useEffect, useRef, useState, useCallback } from 'react';
import { attachCoordAdapter, DATUM_WGS84 } from '../utils/dashboard/basemapCoord';

// MapView组件支持多地图源切换 - 修复版本
// props: mapType(地图类型)、center([lng,lat])、zoom、marker({lng,lat})
function MapView({ mapType = 'mapbox', center = [119.13643, 28.07741], zoom = 10, marker }) {
  const mapboxRef = useRef(null); // Mapbox容器
  const amapRef = useRef(null);   // 高德地图容器
  const bmapRef = useRef(null);   // 百度地图容器
  const gmapRef = useRef(null);   // 谷歌地图容器
  const mapInstance = useRef(null); // 存储地图实例
  
  // 状态管理 - 修复版本
  const [isLoading, setIsLoading] = useState(true); // 地图加载状态
  const [loadError, setLoadError] = useState(null); // 加载错误信息
  const [mapReady, setMapReady] = useState(false); // 地图是否准备就绪
  const [initAttempts, setInitAttempts] = useState(0); // 初始化尝试次数
  const resizeObserver = useRef(null); // 容器尺寸观察器
  const initTimeoutRef = useRef(null); // 初始化超时引用

  // 检查容器是否可用 - 修复版本
  const isContainerReady = useCallback((containerRef) => {
    if (!containerRef || !containerRef.current) return false;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // 检查容器是否有有效尺寸，增加最小尺寸要求
    return rect.width >= 200 && rect.height >= 200;
  }, []);

  // 等待容器准备就绪 - 修复版本
  const waitForContainer = useCallback((containerRef, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      if (isContainerReady(containerRef)) {
        resolve();
        return;
      }

      const startTime = Date.now();
      
      // 创建ResizeObserver监听容器尺寸变化
      if (!resizeObserver.current) {
        resizeObserver.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentRect.width >= 200 && entry.contentRect.height >= 200) {
              resolve();
              return;
            }
          }
        });
      }
      
      // 开始观察容器
      if (containerRef.current) {
        resizeObserver.current.observe(containerRef.current);
      }

      // 设置超时
      const timeoutId = setTimeout(() => {
        if (resizeObserver.current) {
          resizeObserver.current.disconnect();
        }
        reject(new Error('容器准备超时'));
      }, timeout);

      // 定期检查
      const checkInterval = setInterval(() => {
        if (isContainerReady(containerRef)) {
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          if (resizeObserver.current) {
            resizeObserver.current.disconnect();
          }
          resolve();
        }
        
        // 超时检查
        if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          if (resizeObserver.current) {
            resizeObserver.current.disconnect();
          }
          reject(new Error('容器准备超时'));
        }
      }, 200); // 增加检查间隔，减少频闪
    });
  }, [isContainerReady]);

  // 强制触发地图重绘 - 修复版本
  const triggerMapResize = useCallback(() => {
    if (mapInstance.current) {
      try {
        // 对于Mapbox地图
        if (mapInstance.current.resize) {
          mapInstance.current.resize();
        }
        // 对于高德地图
        if (mapInstance.current.setFitView) {
          mapInstance.current.setFitView();
        }
        // 对于百度地图
        if (mapInstance.current.reset) {
          mapInstance.current.reset();
        }
        // 对于谷歌地图
        if (mapInstance.current.setZoom) {
          mapInstance.current.setZoom(mapInstance.current.getZoom());
        }
      } catch (error) {
        console.warn('地图重绘失败:', error);
      }
    }
  }, []);

  // 地图初始化函数 - 修复版本
  const initMap = useCallback(async () => {
    try {
      console.log('🗺️ 开始初始化地图:', mapType);
      
      // 等待容器准备就绪
      const containerRef = getContainerRef();
      if (!containerRef) {
        throw new Error('无效的地图类型');
      }
      
      await waitForContainer(containerRef);
      console.log('✅ 容器准备就绪');
      
      // 根据地图类型初始化
      switch (mapType) {
        case 'mapbox':
        case 'satellite':
        case 'satelliteStreets':
          await initMapboxMap();
          break;
        case 'amap':
          await initAMap();
          break;
        case 'bmap':
          await initBMap();
          break;
        case 'gmap':
          await initGMap();
          break;
        default:
          throw new Error(`不支持的地图类型: ${mapType}`);
      }
      
      console.log('✅ 地图初始化完成');
      setMapReady(true);
      setIsLoading(false);
      
    } catch (error) {
      console.error('❌ 地图初始化失败:', error);
      setLoadError('地图初始化失败: ' + error.message);
      setIsLoading(false);
      
      // 重试机制
      if (initAttempts < 3) {
        console.log(`🔄 尝试重新初始化 (${initAttempts + 1}/3)`);
        setInitAttempts(prev => prev + 1);
        
        // 延迟重试
        setTimeout(() => {
          if (mapInstance.current) {
            initMap();
          }
        }, 2000);
      }
    }
  }, [mapType, center, zoom, marker, waitForContainer, initAttempts]);

  // 获取容器引用
  const getContainerRef = useCallback(() => {
    switch (mapType) {
      case 'mapbox':
      case 'satellite':
      case 'satelliteStreets':
        return mapboxRef;
      case 'amap':
        return amapRef;
      case 'bmap':
        return bmapRef;
      case 'gmap':
        return gmapRef;
      default:
        return null;
    }
  }, [mapType]);

  // Mapbox地图初始化 - 修复版本
  const initMapboxMap = async () => {
    try {
      console.log('🗺️ 开始加载Mapbox库...');
      const mapboxgl = (await import('mapbox-gl')).default;
      console.log('✅ Mapbox库加载成功:', mapboxgl);
      
      if (!mapboxRef.current) {
        console.log('❌ 地图容器引用不存在');
        return;
      }
      
      // 设置访问令牌（运行时配置 / 环境变量 / localStorage；无则降级高德）
      console.log('🔑 设置Mapbox访问令牌...');
      const { getMapboxToken, applyMapboxToken } = await import('../utils/mapboxToken');
      const token = getMapboxToken();
      if (!token) {
        console.warn('未配置 Mapbox Token，自动降级为高德地图');
        await initAMap();
        return;
      }
      applyMapboxToken(mapboxgl);
      
      // 创建地图实例
      const map = new mapboxgl.Map({
        container: mapboxRef.current,
        style: mapType === 'satellite' ? 'mapbox://styles/mapbox/satellite-v9' :
               mapType === 'satelliteStreets' ? 'mapbox://styles/mapbox/satellite-streets-v12' :
               'mapbox://styles/mapbox/streets-v12',
        center: center,
        zoom: zoom,
        attributionControl: false
      });
      // Mapbox 分支为 WGS84：显式声明，不沿用指挥页底图状态
      attachCoordAdapter(map, { datum: DATUM_WGS84, basemapId: 'mapview-mapbox' });
      
      // 存储地图实例
      mapInstance.current = map;
      
      // 添加标记
      if (marker) {
        new mapboxgl.Marker()
          .setLngLat([marker.lng, marker.lat])
          .addTo(map);
      }
      
      // 监听地图加载完成事件
      map.on('load', () => {
        console.log('✅ Mapbox地图加载完成');
        setMapReady(true);
        setIsLoading(false);
      });
      
      // 监听地图错误事件
      map.on('error', (error) => {
        console.error('❌ Mapbox地图错误:', error);
        setLoadError('Mapbox地图加载错误');
        setIsLoading(false);
      });
      
    } catch (error) {
      console.error('❌ Mapbox地图初始化失败:', error);
      throw error;
    }
  };

  // 高德地图初始化 - 修复版本
  const initAMap = async () => {
    try {
      // 动态加载高德地图SDK
      if (typeof AMap === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `https://webapi.amap.com/maps?v=1.4.15&key=y52672bdd6b614c78516c83e07386a0b9`;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      
      if (!amapRef.current) return;
      
      // 创建地图实例
      const map = new AMap.Map(amapRef.current, {
        zoom: zoom,
        center: center,
        mapStyle: 'amap://styles/normal'
      });
      
      // 存储地图实例
      mapInstance.current = map;
      
      // 添加标记
      if (marker) {
        new AMap.Marker({
          position: [marker.lng, marker.lat]
        }).addTo(map);
      }
      
      // 监听地图加载完成事件
      map.on('complete', () => {
        console.log('✅ 高德地图加载完成');
        setMapReady(true);
        setIsLoading(false);
      });
      
    } catch (error) {
      console.error('❌ 高德地图初始化失败:', error);
      throw error;
    }
  };

  // 百度地图初始化 - 修复版本
  const initBMap = async () => {
    try {
      // 动态加载百度地图SDK
      if (typeof BMap === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `https://api.map.baidu.com/api?v=3.0&ak=your_baidu_map_key`;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      
      if (!bmapRef.current) return;
      
      // 创建地图实例
      const map = new BMap.Map(bmapRef.current);
      const point = new BMap.Point(center[0], center[1]);
      map.centerAndZoom(point, zoom);
      map.enableScrollWheelZoom();
      
      // 存储地图实例
      mapInstance.current = map;
      
      // 添加标记
      if (marker) {
        const markerPoint = new BMap.Point(marker.lng, marker.lat);
        new BMap.Marker(markerPoint).addTo(map);
      }
      
      // 监听地图加载完成事件
      map.addEventListener('tilesloaded', () => {
        console.log('✅ 百度地图加载完成');
        setMapReady(true);
        setIsLoading(false);
      });
      
    } catch (error) {
      console.error('❌ 百度地图初始化失败:', error);
      throw error;
    }
  };

  // 谷歌地图初始化 - 修复版本
  const initGMap = async () => {
    try {
      // 动态加载谷歌地图SDK
      if (typeof google === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=your_google_maps_key`;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      
      if (!gmapRef.current) return;
      
      // 创建地图实例
      const map = new google.maps.Map(gmapRef.current, {
        zoom: zoom,
        center: { lat: center[1], lng: center[0] },
        mapTypeId: google.maps.MapTypeId.ROADMAP
      });
      
      // 存储地图实例
      mapInstance.current = map;
      
      // 添加标记
      if (marker) {
        new google.maps.Marker({
          position: { lat: marker.lat, lng: marker.lng },
          map: map
        });
      }
      
      // 监听地图加载完成事件
      google.maps.event.addListenerOnce(map, 'idle', () => {
        console.log('✅ 谷歌地图加载完成');
        setMapReady(true);
        setIsLoading(false);
      });
      
    } catch (error) {
      console.error('❌ 谷歌地图初始化失败:', error);
      throw error;
    }
  };

  // 主要初始化逻辑 - 修复版本
  useEffect(() => {
    let isMounted = true;
    
    // 清理之前的超时
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }
    
    // 重置状态
    setIsLoading(true);
    setLoadError(null);
    setMapReady(false);
    
    // 延迟初始化，避免频闪
    initTimeoutRef.current = setTimeout(() => {
      if (isMounted) {
        initMap();
      }
    }, 500);
    
    // 清理函数
    return () => {
      isMounted = false;
      
      // 清理超时
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      
      // 清理地图实例
      if (mapInstance.current) {
        try {
          if (mapInstance.current.remove) {
            mapInstance.current.remove();
          }
          mapInstance.current = null;
        } catch (error) {
          console.warn('清理地图实例失败:', error);
        }
      }
      
      // 清理观察器
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
      
      // 重置状态
      setMapReady(false);
      setIsLoading(false);
      setLoadError(null);
    };
  }, [mapType, center[0], center[1], zoom, initMap]);

  // 监听窗口尺寸变化 - 修复版本
  useEffect(() => {
    const handleResize = () => {
      if (mapReady && mapInstance.current) {
        console.log('🔄 窗口尺寸变化，重新绘制地图');
        // 增加延迟，减少频闪
        setTimeout(() => {
          if (mapInstance.current) {
            triggerMapResize();
          }
        }, 300);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mapReady, triggerMapResize]);

  // 渲染不同地图容器 - 修复版本
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', minHeight: '400px' }}>
      {/* 加载状态指示器 - 修复版本 */}
      {isLoading && !mapReady && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          textAlign: 'center',
          minWidth: '150px'
        }}>
          <div style={{ marginBottom: '15px', fontSize: '24px' }}>🗺️</div>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>地图加载中...</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            尝试 {initAttempts + 1}/3
          </div>
        </div>
      )}
      
      {/* 错误状态显示 - 修复版本 */}
      {loadError && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'rgba(255, 200, 200, 0.95)',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          textAlign: 'center',
          maxWidth: '300px'
        }}>
          <div style={{ marginBottom: '15px', fontSize: '24px' }}>❌</div>
          <div style={{ marginBottom: '15px', fontWeight: 'bold' }}>地图加载失败</div>
          <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
            {loadError}
          </div>
          <button
            onClick={() => {
              setLoadError(null);
              setInitAttempts(0);
              initMap();
            }}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            重试
          </button>
        </div>
      )}
      
      {/* 地图容器 - 修复版本 */}
      <div
        ref={mapboxRef}
        style={{
          display: (mapType === 'mapbox' || mapType === 'satellite' || mapType === 'satelliteStreets') ? 'block' : 'none',
          width: '100%',
          height: '100%',
          minHeight: '400px'
        }}
      />
      
      <div
        ref={amapRef}
        style={{
          display: mapType === 'amap' ? 'block' : 'none',
          width: '100%',
          height: '100%',
          minHeight: '400px'
        }}
      />
      
      <div
        ref={bmapRef}
        style={{
          display: mapType === 'bmap' ? 'block' : 'none',
          width: '100%',
          height: '100%',
          minHeight: '400px'
        }}
      />
      
      <div
        ref={gmapRef}
        style={{
          display: mapType === 'gmap' ? 'block' : 'none',
          width: '100%',
          height: '100%',
          minHeight: '400px'
        }}
      />
    </div>
  );
}

export default MapView;
